import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, ImagePlus, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { TextField } from "../shared/FormField";
import { AudiencePicker } from "./AudiencePicker";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

export function OtherUpdateCard({ onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [audience, setAudience] = useState("active");
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await apiClient.post("/daily-menu/upload-image", fd);
      setImage(data.path);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not upload the image."));
      setPreviewUrl("");
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => { setImage(""); setPreviewUrl(""); if (fileRef.current) fileRef.current.value = ""; };

  const send = async (e) => {
    e.preventDefault();
    if (audience === "custom" && selectedIds.length === 0) {
      toast.error("Select at least one customer, or pick a different audience.");
      return;
    }
    if (!image && !body && !title) {
      toast.error("Add an image or a message to send.");
      return;
    }
    setSending(true);
    try {
      await apiClient.post("/customer-updates", {
        title: title || null, body: body || null, image: image || null,
        audience, customer_ids: audience === "custom" ? selectedIds : [],
      });
      toast.success("Update sent to customers.");
      setTitle(""); setBody(""); clearImage(); setSelectedIds([]);
      onSent?.();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not send this update."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="other-update-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ImageIcon size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Send an Update</p>
            <p className="mt-0.5 text-xs text-text-muted">A picture or message shown under Today's Menu and in the customer's notification bell.</p>
          </div>
        </div>

        <form onSubmit={send} className="mt-4 space-y-4">
          <TextField label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} testId="other-update-title" />
          <div className="space-y-1.5">
            <Label htmlFor="other-update-body">Message (optional)</Label>
            <Textarea id="other-update-body" data-testid="other-update-body" rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Our kitchen is closed on Sunday — enjoy the break!" maxLength={255} />
          </div>

          <div className="space-y-1.5">
            <Label>Photo (optional)</Label>
            {previewUrl ? (
              <div className="relative w-full max-w-[200px]">
                <img src={previewUrl} alt="Update" data-testid="other-update-preview" className="aspect-[4/3] w-full rounded-md border border-border object-cover" />
                <button type="button" onClick={clearImage} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-danger text-white shadow" data-testid="other-update-image-remove">
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            ) : (
              <label htmlFor="other-update-input" className="flex h-24 w-full max-w-[200px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-surface text-text-muted hover:bg-background">
                <ImagePlus size={18} strokeWidth={1.5} />
                <span className="text-xs">Upload photo</span>
              </label>
            )}
            <input ref={fileRef} id="other-update-input" type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="other-update-input" />
            {uploading && <p className="text-xs text-text-muted">Uploading...</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Audience</Label>
            <AudiencePicker audience={audience} setAudience={setAudience} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
          </div>

          <Button type="submit" variant="accent" className="w-full" disabled={sending || uploading} data-testid="other-update-submit">
            {sending ? "Sending..." : "Send Update"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
